import type {
  DeterministicToolResultMetadata,
  PlanConfidence,
  RuleSource,
  SunlightBand,
  ToolEngineMetadata,
} from './types'
import {
  buildPlanConfidence,
  normalizeGrassType,
  normalizeLawnProfile,
  normalizeSunlight,
  parseLawnSizeSqft,
} from './normalization'
import { buildInputEffects } from './rules'
import { RULE_SOURCES } from './sources'
import { LAWN_PLAN_ENGINE_VERSION } from './types'

export type CanonicalToolId =
  | 'fertilizer'
  | 'watering'
  | 'mowing'
  | 'seeding'
  | 'aeration'
  | 'herbicide'
  | 'cost'
  | 'grass_comparison'
  | 'lawn_size'
  | 'soil_temperature'
  | 'dethatching'

export interface CanonicalToolContext {
  toolId: CanonicalToolId
  grassType: string
  grassTypeRaw: string | null
  grassTypeKnown: boolean
  grassTypeAmbiguous: boolean
  grassTypeAlternatives: string[]
  sunlight: SunlightBand | null
  sunlightRaw: string | null
  lawnSizeSqft: number | null
  lawnSizeRaw: string | null
  confidence: PlanConfidence
  metadata: ToolEngineMetadata
}

export interface BuildCanonicalToolContextInput {
  toolId: CanonicalToolId
  grassType?: string | null
  sunlight?: string | null
  lawnSize?: string | number | null
  hasWeather?: boolean
  hasHistory?: boolean
}

export interface ToolCoverageEntry {
  toolId: CanonicalToolId
  canonicalInputs: Array<'grass_type' | 'sunlight' | 'lawn_size' | 'zip' | 'soil_type' | 'weather'>
  sunlightSupport: 'affects-output' | 'metadata-only' | 'none'
  sqftSupport: 'affects-output' | 'metadata-only' | 'none'
  sourceIds: string[]
}

export const TOOL_COVERAGE: ToolCoverageEntry[] = [
  {
    toolId: 'fertilizer',
    canonicalInputs: ['grass_type', 'sunlight', 'lawn_size', 'soil_type'],
    sunlightSupport: 'affects-output',
    sqftSupport: 'affects-output',
    sourceIds: ['purdue-fertilizer-2026', 'rutgers-fertilizer-math-2026', 'okstate-shade-2026'],
  },
  {
    toolId: 'watering',
    canonicalInputs: ['grass_type', 'sunlight', 'lawn_size', 'soil_type', 'weather'],
    sunlightSupport: 'affects-output',
    sqftSupport: 'affects-output',
    sourceIds: ['okstate-shade-2026', 'uf-ifas-shade-2026', 'umd-established-lawn-2026'],
  },
  {
    toolId: 'mowing',
    canonicalInputs: ['grass_type', 'sunlight'],
    sunlightSupport: 'affects-output',
    sqftSupport: 'none',
    sourceIds: ['okstate-shade-2026', 'umd-established-lawn-2026'],
  },
  {
    toolId: 'seeding',
    canonicalInputs: ['grass_type', 'lawn_size', 'zip'],
    sunlightSupport: 'metadata-only',
    sqftSupport: 'metadata-only',
    sourceIds: ['rutgers-fertilizer-math-2026'],
  },
  {
    toolId: 'aeration',
    canonicalInputs: ['grass_type', 'zip'],
    sunlightSupport: 'metadata-only',
    sqftSupport: 'metadata-only',
    sourceIds: ['umd-established-lawn-2026'],
  },
  {
    toolId: 'dethatching',
    canonicalInputs: ['grass_type', 'zip', 'lawn_size'],
    sunlightSupport: 'metadata-only',
    sqftSupport: 'affects-output',
    sourceIds: ['umd-established-lawn-2026'],
  },
  {
    toolId: 'herbicide',
    canonicalInputs: ['grass_type', 'zip'],
    sunlightSupport: 'metadata-only',
    sqftSupport: 'metadata-only',
    sourceIds: ['umd-established-lawn-2026'],
  },
  {
    toolId: 'cost',
    canonicalInputs: ['grass_type', 'sunlight', 'lawn_size', 'zip'],
    sunlightSupport: 'metadata-only',
    sqftSupport: 'affects-output',
    sourceIds: ['rutgers-fertilizer-math-2026', 'okstate-shade-2026'],
  },
  {
    toolId: 'grass_comparison',
    canonicalInputs: ['grass_type', 'sunlight', 'zip'],
    sunlightSupport: 'metadata-only',
    sqftSupport: 'none',
    sourceIds: ['okstate-shade-2026', 'uf-ifas-shade-2026', 'missouri-shade-2026'],
  },
  {
    toolId: 'lawn_size',
    canonicalInputs: ['lawn_size'],
    sunlightSupport: 'none',
    sqftSupport: 'affects-output',
    sourceIds: ['rutgers-fertilizer-math-2026'],
  },
  {
    toolId: 'soil_temperature',
    canonicalInputs: ['zip', 'weather'],
    sunlightSupport: 'none',
    sqftSupport: 'none',
    sourceIds: ['umd-established-lawn-2026'],
  },
]

export function buildCanonicalToolContext(input: BuildCanonicalToolContextInput): CanonicalToolContext {
  const profile = normalizeLawnProfile({
    lawn: {
      id: `tool:${input.toolId}`,
      grass_type: input.grassType ?? null,
      sunlight: input.sunlight ?? null,
      lawn_size: input.lawnSize ?? null,
    },
  })
  const inputEffects = buildInputEffects({
    profile,
    hasWeather: Boolean(input.hasWeather),
    hasHistory: Boolean(input.hasHistory),
  })
  const confidence = buildPlanConfidence({
    profile,
    hasWeather: Boolean(input.hasWeather),
    hasHistory: Boolean(input.hasHistory),
    inputEffects,
  })
  const metadata: ToolEngineMetadata = {
    toolId: input.toolId,
    engineVersion: LAWN_PLAN_ENGINE_VERSION,
    deterministic: true,
    ruleSources: RULE_SOURCES,
    inputEffects,
    confidence,
  }

  return {
    toolId: input.toolId,
    grassType: profile.grassType,
    grassTypeRaw: profile.grassTypeRaw,
    grassTypeKnown: profile.grassTypeResolution.isKnown,
    grassTypeAmbiguous: profile.grassTypeResolution.ambiguous,
    grassTypeAlternatives: profile.grassTypeResolution.alternatives,
    sunlight: profile.sunlight,
    sunlightRaw: profile.sunlightRaw,
    lawnSizeSqft: profile.lawnSizeSqft,
    lawnSizeRaw: profile.lawnSizeRaw,
    confidence,
    metadata,
  }
}

export function attachToolEngineMetadata<T extends object>(
  result: T,
  context: CanonicalToolContext,
): T & DeterministicToolResultMetadata {
  return {
    ...result,
    engineVersion: context.metadata.engineVersion,
    deterministic: true,
    ruleSources: context.metadata.ruleSources,
    inputEffects: context.metadata.inputEffects,
    confidence: context.metadata.confidence,
    toolEngine: context.metadata,
  }
}

export function buildCanonicalToolContextFromLooseInputs(
  toolId: CanonicalToolId,
  inputs: Record<string, unknown> | null | undefined,
  result?: Record<string, unknown> | null,
): CanonicalToolContext {
  const grassType =
    pickString(inputs, ['grassType', 'grass_type', 'grass']) ??
    pickString(result, ['grassType', 'grass_type', 'grass'])
  const sunlight =
    pickString(inputs, ['sunlight', 'sun', 'shade']) ??
    pickString(result, ['sunlight', 'sun', 'shade'])
  const lawnSize =
    pickNumberOrString(inputs, ['lawnSize', 'lawn_size', 'lawnSizeSqft', 'lawn_size_sqft', 'size', 'sqft']) ??
    pickNumberOrString(result, ['lawnSize', 'lawn_size', 'lawnSizeSqft', 'lawn_size_sqft', 'size', 'sqft'])

  return buildCanonicalToolContext({
    toolId,
    grassType,
    sunlight,
    lawnSize,
    hasWeather: toolId === 'watering' || toolId === 'soil_temperature',
    hasHistory: false,
  })
}

export function canonicalizeToolGrassType(raw?: string | null): string {
  return normalizeGrassType(raw).canonical
}

export function canonicalizeToolSunlight(raw?: string | null): SunlightBand | null {
  return normalizeSunlight(raw).canonical
}

export function canonicalizeToolLawnSize(raw?: string | number | null): number | null {
  return parseLawnSizeSqft(raw).sqft
}

export function toolCoverageReference(): Array<ToolCoverageEntry & { ruleSources: RuleSource[] }> {
  return TOOL_COVERAGE.map((entry) => ({
    ...entry,
    ruleSources: RULE_SOURCES.filter((source) => entry.sourceIds.includes(source.id)),
  }))
}

function pickString(source: Record<string, unknown> | null | undefined, keys: string[]): string | null {
  for (const key of keys) {
    const value = source?.[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return null
}

function pickNumberOrString(
  source: Record<string, unknown> | null | undefined,
  keys: string[],
): string | number | null {
  for (const key of keys) {
    const value = source?.[key]
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return null
}
